import { useMemo } from 'react';
import PropTypes from 'prop-types';
import _reject from 'lodash/reject';
import ListIcon from '@material-ui/icons/List';
import TodayIcon from '@material-ui/icons/Today';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import Tabs from '../tabs';
import FilterBar from '../calendar/filter-bar';
import { filterableRoute } from '../../utils/routes';
import MaterialLinkButton from '../material-link-button';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0, 1, 0),
    [theme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
  },
  headerLeft: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    margin: 0,
    paddingRight: theme.spacing(2),
  },
  addButton: {
    marginRight: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  addIcon: {
    marginRight: theme.spacing(0.5),
  },
  tabs: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  titleLink: {
    color: 'rgba(0, 0, 0, 0.87)',
    textDecoration: 'none',
  },
  label: {
    display: 'flex',
    alignItems: 'center',
  },
  labelIcon: {
    marginRight: theme.spacing(0.5),
  },
}));

const CalendarHeader = ({ group, title, tab, filters }) => {
  const classes = useStyles();

  const basePath = useMemo(() => {
    if (tab === 'list' || !tab) {
      return '/calendar';
    }
    return `/calendar/${tab}`;
  }, [tab]);

  const tabOptions = useMemo(() => {
    const filterableFilters =  _reject(filters, 'filter');
    return [
      {
        label: (
          <span className={classes.label}>
            <ListIcon className={classes.labelIcon} />
            List
          </span>
        ),
        value: 'list',
        href: filterableRoute(`/groups/[slug]/calendar`, filterableFilters),
        as: filterableRoute(`/groups/${group.slug}/calendar`, filterableFilters),
      },
      {
        label: (
          <span className={classes.label}>
            <TodayIcon className={classes.labelIcon} />
            Grid
          </span>
        ),
        value: 'grid',
        href: filterableRoute(`/groups/[slug]/calendar/grid`, filterableFilters),
        as: filterableRoute(`/groups/${group.slug}/calendar/grid`, filterableFilters),
      },
    ];
  }, [group, filters])

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <Typography variant="h1" className={classes.title}>
            {title}
          </Typography>
          {group.permissions.createEvent && (
            <MaterialLinkButton
              href={`/events/new?groupId=${group.id}`}
              color="primary"
              variant="contained"
              className={classes.addButton}
              size="small"
            >
              <AddIcon className={classes.addIcon} />
              Add Event
            </MaterialLinkButton>
          )}
        </div>

        <Tabs
          options={tabOptions}
          tab={tab}
          className={classes.tabs}
          inverted
          links
        />
      </div>
      {filters && (
        <div className={classes.filterBar}>
          <FilterBar
            filters={filters}
            tab={tab}
            basePath={basePath}
            baseHref="/groups/[slug]"
            baseAs={`/groups/${group.slug}`}
          />
        </div>
      )}
    </div>
  );
};

CalendarHeader.propTypes = {
  title: PropTypes.node.isRequired,
  tab: PropTypes.string,
  filters: PropTypes.object,
  group: PropTypes.object.isRequired,
};

export default CalendarHeader;
